import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';

import { filter, Observable, of, switchMap, take, withLatestFrom } from 'rxjs';

import { CoreStoreService } from '@web/core/services/core-store.service';
import { VIRoutingMap } from '@web/app/routing/routes';
import { EdgeExtensionsStoreService } from '@web/core/services/edge-extensions-store.service';
import { ProvisioningState } from '@web/core/interfaces';

import { FeatureSwitch } from '@common/modules/core/services/interfaces';
import { FeatureSwitchService } from '@common/modules/core/services/feature-switch/feature-switch.service';
import { GuidPattern } from '@common/modules/utils/regexes';
import { StorageCacheKey } from '@common/modules/shared/interfaces';
import { LocalStorageService } from '@common/modules/shared/services/local-storage.service';

export const LiveEdgeGuard: ResolveFn<Observable<boolean>> = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
  return inject(LiveEdgeGuardService).canActivate(route);
};

@Injectable({
  providedIn: 'root'
})
export class LiveEdgeGuardService {
  constructor(
    private featureSwitchService: FeatureSwitchService,
    private coreStore: CoreStoreService,
    private edgeExtensionsStore: EdgeExtensionsStoreService,
    protected localStorageService: LocalStorageService
  ) {}

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    if (!this.featureSwitchService.featureSwitch(FeatureSwitch.LiveEdge)) {
      return this.canNotActivate();
    }
    return this.edgeExtensionsStore.edgeExtensionsMetadataLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      withLatestFrom(this.coreStore.selectAccountIds$),
      switchMap(([loaded, accountIds]) => this.checkAccountExistence$(route, accountIds)),
      switchMap(isAccountExist => (isAccountExist ? this.checkIsExtensionExsists$(route) : of(false))),
      switchMap(isExtensionExists => (isExtensionExists ? this.checkIsLiveEnabledForExtension$(route) : of(false))),
      switchMap(isLiveEnabled => (isLiveEnabled ? this.checkExtensionProvisioningState$(route) : of(false))),
      switchMap(isIotResourceHub => (isIotResourceHub ? this.checkLiveModuleLoaded$() : of(false)))
    );
  }

  /**
   * Check if the account ID from the route parameters exists in the logged-in user's account IDs.
   * If the account belongs to the user, selects the account and loads the edge extensions.
   * Otherwise, the route cannot be activated.
   */
  private checkAccountExistence$(route: ActivatedRouteSnapshot, accountIds: string[]): Observable<boolean> {
    const accountId = route?.params?.accountId;
    if (!accountIds.includes(accountId)) {
      return this.canNotActivate();
    }
    this.coreStore.selectAccount(accountId, false, true);

    return this.edgeExtensionsStore.selectEdgeExtensionsLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(() => of(true))
    );
  }

  private checkIsExtensionExsists$(route: ActivatedRouteSnapshot): Observable<boolean> {
    const extensionId = route?.params?.extensionId;
    const regex = new RegExp(`${GuidPattern}`);
    const match = extensionId?.match(regex);
    if (!match) {
      return this.canNotActivate();
    }

    return this.edgeExtensionsStore.selectEdgeExtensionsLoaded$.pipe(
      filter(loaded => loaded),
      withLatestFrom(this.edgeExtensionsStore.selectEdgeExtensions$),
      withLatestFrom(this.coreStore.currentTenantId$),
      switchMap(([[_loaded, extensions], tenantId]) => {
        const extensionExists = extensions.some(ext => ext.identity?.principalId === extensionId);
        if (!extensionExists) {
          this.localStorageService.removeItem(`${StorageCacheKey.LastExtension}_${tenantId}`);
          return this.canNotActivate();
        }
        return of(true);
      })
    );
  }

  private checkIsLiveEnabledForExtension$(route: ActivatedRouteSnapshot): Observable<boolean> {
    const extensionId = route?.params?.extensionId;

    this.edgeExtensionsStore.selectEdgeExtensionByPrincipalId(extensionId);

    return this.edgeExtensionsStore.selectedEdgeExtension$.pipe(
      filter(extension => extension !== undefined),
      withLatestFrom(this.edgeExtensionsStore.isLiveStreamEnabled$),
      switchMap(([selectedEdgeExtension, isLiveStreamEnabled]) => {
        if (selectedEdgeExtension && !isLiveStreamEnabled) {
          return this.canNotActivate();
        }

        return of(true);
      })
    );
  }

  private checkExtensionProvisioningState$(route: ActivatedRouteSnapshot): Observable<boolean> {
    const extensionId = route?.params?.extensionId;
    this.edgeExtensionsStore.selectEdgeExtensionByPrincipalId(extensionId);

    return this.edgeExtensionsStore.selectedEdgeExtension$.pipe(
      filter(extension => extension !== undefined),
      switchMap(selectedEdgeExtension => {
        if (selectedEdgeExtension.properties.provisioningState !== ProvisioningState.Succeeded) {
          this.coreStore.navigate([`/${VIRoutingMap.edgeExtensions.path}`]);
          return of(false);
        }

        return of(true);
      })
    );
  }

  /**
   * Checks if the live mode is loaded.
   */
  private checkLiveModuleLoaded$(): Observable<boolean> {
    return this.edgeExtensionsStore.isLiveModuleLoaded$.pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(() => {
        return of(true);
      })
    );
  }

  private canNotActivate(): Observable<boolean> {
    this.coreStore.navigate([`/${VIRoutingMap.pageNotFoundError.path}`]);
    return of(false);
  }
}
